import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import moment from 'moment';
import { BillsV4Service } from '../../zentility/_shared/_services/v4/bills-v4.service';
import {
  AuthenticationService
} from '../../zentility/_shared/_zen-legacy-common/zen-common-services/_services/authentication.service';
import {
  BillCreateRequest,
  BillDto,
  BillLineItem,
  BillRequest,
  BillType,
  ElecBillDto,
  GasBillDto,
  GasUnitType,
  isElecBill,
  isGasBill,
} from '../../zentility/_shared/_model/bills-v4.model';
import { MeterDetailsV4Model } from '../../zentility/_modules/portfolio/_model/portfolio-meters.model';
import { CommodityType } from '../../zentility/_shared/_zen-legacy-common/_models/commodity';
import { ChargeGroup, ChargeLineItem, ParsedBillData, BillType as ParsedBillType } from '../models';
import { GasUnitName, GAS_UNIT_LABEL } from '../../zentility/_shared/_model/bills-v4-gas.model';

/**
 * Options for building a create/patch payload from the form-shaped {@link ParsedBillData}.
 */
export interface BillPersistenceOptions {
  /** Known meter context — supplies commodity, customer/SA/meter ids, default unit, rate schedule. */
  meterDet: MeterDetailsV4Model;
  /**
   * When true, the per-bucket budgeting fields (contractCharges, energyCharges, …,
   * distributionTax) are written from the ChargeGroup totals. When false those keys are
   * omitted entirely so a JSON Merge Patch leaves any existing bucket values untouched.
   */
  isBudgetingMode: boolean;
  /**
   * EDIT mode — the bill being edited. Untouched fields fall back to this bill's stored
   * values so the merge patch never clobbers data the editor didn't surface.
   */
  existingBill?: BillDto;
  /** Newly uploaded utility-bill temp file id (1st file, CONSOLIDATED & DUAL). */
  tempFileId?: string;
  /** Newly uploaded supplier-bill temp file id (2nd file, DUAL only). */
  supplierTempFileId?: string;
  /** AI request log id of the extraction the bill was read from; omitted keeps an existing bill's link. */
  billAiId?: number;
}

/** Convenience subset of {@link BillPersistenceOptions} for the save wrappers. */
export type BillSaveFileRefs = Pick<BillPersistenceOptions, 'tempFileId' | 'supplierTempFileId' | 'billAiId'>;

/**
 * Fields that must NEVER be sent on create or patch — they are derived/calculated by the
 * backend. Mirrors OMIT_FROM_PATCH in budget-single-bill-upload-dialog.component.ts.
 */
export const BILL_REQUEST_OMIT_FIELDS: ReadonlySet<string> = new Set([
  'supplyProviderRate',
  'contractedRate',
  'distributionRate',
  'totalSupplyRate',
  'id', // never in body
]);

/**
 * Maps between the ai-onboarding {@link ParsedBillData} form model and the BillsV4 API
 * models so a standalone bill editor can CREATE and PATCH bills directly through
 * {@link BillsV4Service}, and LOAD an existing bill back into the form for editing.
 *
 * Mapping semantics intentionally mirror the two existing save paths so all three stay
 * consistent:
 *  - the wizard import path (`buildBillImport` in utils/bill-grouping.util.ts), and
 *  - the budget bill dialog (FIELD_MAPPINGS in budget-single-bill-upload-dialog.component.ts).
 *
 * Line items: per-bucket TOTALS map to the numeric bill columns (e.g. `energyCharges`).
 * The detailed ChargeGroup.lineItems now ALSO round-trip, via the four JSON columns the
 * backend exposes on `bill_elec`/`bill_gas` (general_line_items, usage_line_items,
 * supply_line_items, distribution_line_items). Each column is a FLAT array per section, so
 * the originating ChargeGroup bucket key (e.g. 'supplyEnergyCharges') is encoded in each
 * {@link BillLineItem}'s `lineItemData.category`; {@link toParsedBillData} regroups by that
 * key on load. Line items are written only in budgeting mode (standard mode has no buckets),
 * matching how bucket TOTALS are gated.
 *
 * Backend dependency: sending these arrays is safe before the matching create/PATCH DTO
 * deploys (Spring Boot ignores unknown JSON props). On LOAD they stay empty until the bill
 * RESPONSE DTO surfaces the entity's *LineItems columns — the read path is already wired.
 *
 * billType translation: ParsedBillData.billType is `'utility' | 'supplier' | 'unknown'`
 * while the API uses the {@link BillType} enum. Matching bill-grouping.util semantics,
 * `'utility'` → CONSOLIDATED and everything else (`'supplier'`, `'unknown'`) → DUAL.
 * Loading reverses it: CONSOLIDATED → `'utility'`, DUAL → `'supplier'`.
 *
 * Dates: ParsedBillData carries ISO strings; the API expects `YYYY-MM-DD` (LocalDate).
 * Conversion is explicit and timezone-safe (date-only prefixes are taken verbatim).
 */
@Injectable({ providedIn: 'root' })
export class BillPersistenceService {

  constructor(
    private billsV4Service: BillsV4Service,
    private authSvc: AuthenticationService,
  ) {}

  // ===========================================================================
  // LOAD — BillDto → ParsedBillData (EDIT mode)
  // ===========================================================================

  /**
   * Maps an existing BillsV4 bill into the {@link ParsedBillData} form shape.
   *
   * Entity fields the bills API does not carry (legal entity, addresses, utility/provider
   * names) are returned empty — at meter level the editor already knows that context.
   * Budgeting buckets come back as ChargeGroups whose `total` is the numeric bill column and
   * whose `lineItems` are reconstructed from the bill's supply/distribution JSON line-item
   * arrays, regrouped by each item's `lineItemData.category` (see class doc / {@link BillLineItem}).
   */
  toParsedBillData(bill: BillDto): ParsedBillData {
    const general = bill.generalInformation;
    const ident = bill.identificationInformation;
    const usage = bill.usageInformation;
    const supply = bill.supplyInformation;
    const dist = bill.distributionInformation;

    const elecBill: ElecBillDto | null = isElecBill(bill) ? bill : null;
    const gasBill: GasBillDto | null = isGasBill(bill) ? bill : null;

    const data: ParsedBillData = {
      // Entity context — not stored on bills; known from the meter at this level.
      legalEntity: '',
      serviceAddressStreet1: '',
      serviceAddressStreet2: '',
      serviceAddressCity: '',
      serviceAddressState: '',
      serviceAddressZip: '',

      providerName: ident?.supplierId ? '' : 'On Utility',
      supplierId: ident?.supplierId ?? undefined,
      energyPlanId: ident?.energyPlanId ?? undefined,

      utilityId: ident?.utilityId ?? undefined,
      utilityName: '',
      utilityAccountNumber: ident?.utilityAccountNum1 ?? '',
      utilityAccountNumber2: ident?.utilityAccountNum2 ?? undefined,
      utilityAccountNumber3: ident?.utilityAccountNum3 ?? undefined,
      additionalIdentifiers: [],

      billingPeriodStart: toIsoDateOnly(general?.billStartDate) ?? '',
      billingPeriodEnd: toIsoDateOnly(general?.billEndDate) ?? '',

      totalAmount: general?.periodBillTotal ?? 0,
      currency: 'USD',

      usageAmount: usage?.totalUsage ?? 0,
      // Fall back to the persisted unitId when unitDto is null; the form resolves the name by id
      // (leaving usageUnit empty for both commodities so a non-default unit, e.g. MWh, isn't lost).
      usageUnit: bill.unitDto?.unit ?? '',
      usageUnitId: bill.unitDto?.id ?? ident?.unitId ?? undefined,

      // Supply (standard fields) — absent values stay null (not 0) so a legacy
      // bill that carries only a section total keeps that shape through save.
      supplyTotalCost: supply?.supplyTotal ?? null,
      supplyCharges: supply?.supplyCharges ?? null,
      supplyTaxes: supply?.supplyTax ?? null,

      // Distribution (standard fields)
      distributionTotalCost: dist?.distributionTotal ?? null,
      distributionCharges: dist?.distributionCharges ?? null,
      distributionTaxes: dist?.distributionTax ?? null,

      billType: fromApiBillType(general?.billType),
      isDualBilling: general?.billType === BillType.DUAL,
      confidence: 1, // existing platform bill — user-verified data
    };

    // --- Elec-only fields ---
    if (elecBill) {
      const eu = elecBill.usageInformation;
      const es = elecBill.supplyInformation;
      const ed = elecBill.distributionInformation;
      data.demandKw = eu?.demand ?? undefined;
      data.onPeakUsage = eu?.onPeakUsage ?? undefined;
      data.onPeakCost = eu?.onPeakCost ?? undefined;
      data.intermediateUsage = eu?.intermediateUsage ?? undefined;
      data.intermediateCost = eu?.intermediateCost ?? undefined;
      data.offPeakUsage = eu?.offPeakUsage ?? undefined;
      data.offPeakCost = eu?.offPeakCost ?? undefined;

      // Supply budgeting buckets (totals only — see class doc on line items)
      data.supplyContractCharges = toChargeGroup(es?.contractCharges);
      data.supplyEnergyCharges = toChargeGroup(es?.energyCharges);
      data.supplyCapacityCharges = toChargeGroup(es?.capacityCharges);
      data.supplyTransmissionCharges = toChargeGroup(es?.transmissionCharges);
      data.supplyMarketCharges = toChargeGroup(es?.marketCharges);
      data.supplyLineLossCharges = toChargeGroup(es?.lineLossCharges);
      data.supplyGrtSut = toChargeGroup(es?.grtSut);
      data.supplyMiscCharges = toChargeGroup(es?.supplyMisc);
      data.supplySalesTaxes = toChargeGroup(es?.supplyTax);

      // Distribution budgeting buckets
      data.distributionCustomerCharges = toChargeGroup(ed?.distributionCustomerCharges);
      data.distributionEnergyCharges = toChargeGroup(ed?.distributionEnergyCharges);
      data.distributionDemandCharges = toChargeGroup(ed?.distributionDemandCharges);
      data.distributionMiscCharges = toChargeGroup(ed?.distributionMisc);
      data.distributionTaxCharges = toChargeGroup(ed?.distributionTax);
    }

    // --- Gas-only fields ---
    if (gasBill) {
      const gs = gasBill.supplyInformation;
      const gd = gasBill.distributionInformation;
      // Supply budgeting buckets
      data.supplyContractCharges = toChargeGroup(gs?.contractCharges);
      data.supplyCustomerCharges = toChargeGroup(gs?.supplyCustomerCharges);
      data.supplyGasCharges = toChargeGroup(gs?.supplyGasCharges);
      data.supplyOffPeakCharges = toChargeGroup(gs?.supplyOffPeakCharges);
      data.supplyRateClassCharges = toChargeGroup(gs?.supplyRateClassCharges);
      data.supplyGrtSut = toChargeGroup(gs?.supplyGrtSut);
      data.supplyMiscCharges = toChargeGroup(gs?.supplyMisc);
      data.supplySalesTaxes = toChargeGroup(gs?.supplyTax);

      // Distribution budgeting buckets
      data.distributionCustomerCharges = toChargeGroup(gd?.distributionCustomerCharges);
      data.distributionGasCharges = toChargeGroup(gd?.distributionGasCharges);
      data.distributionDeliveryCharges = toChargeGroup(gd?.distributionDeliveryCharges);
      data.distributionOffPeakCharges = toChargeGroup(gd?.distributionOffPeakCharges);
      data.distributionBalancingCharges = toChargeGroup(gd?.distributionBalancingCharges);
      data.distributionRateClassCharges = toChargeGroup(gd?.distributionRateClassCharges);
      data.distributionGrtSut = toChargeGroup(gd?.distributionGrtSut);
      data.distributionMiscCharges = toChargeGroup(gd?.distributionMisc);
      data.distributionTaxCharges = toChargeGroup(gd?.distributionTax);
    }

    // Overlay charge-bucket line items from the JSON arrays (flat per section), regrouping
    // by lineItemData.category into each ChargeGroup. Numeric totals above stay authoritative.
    applyLineItemsToGroups(data, bill.supplyLineItems, SUPPLY_BUCKET_KEYS);
    applyLineItemsToGroups(data, bill.distributionLineItems, DISTRIBUTION_BUCKET_KEYS);

    return data;
  }

  // ===========================================================================
  // BUILD — ParsedBillData → BillRequest payload (ADD/EDIT save)
  // ===========================================================================

  /**
   * Builds a create/patch payload from the form-shaped {@link ParsedBillData}.
   *
   * ADD mode (no `existingBill`): only fields with values are included.
   * EDIT mode: starts from `existingBill`'s stored values and overlays defined form values,
   * so untouched fields keep their server value in the merge patch. Ids and backend-derived
   * fields ({@link BILL_REQUEST_OMIT_FIELDS}) are never included.
   */
  buildBillRequest(data: ParsedBillData, opts: BillPersistenceOptions): Partial<BillRequest> {
    const { meterDet, isBudgetingMode, existingBill } = opts;
    const commodity = meterDet.commodityType;
    const isElec = commodity === CommodityType.Electricity;

    const general = existingBill?.generalInformation;
    const ident = existingBill?.identificationInformation;
    const usage = existingBill?.usageInformation;
    const supply = existingBill?.supplyInformation;
    const dist = existingBill?.distributionInformation;
    const elecOld: ElecBillDto | null = existingBill && isElecBill(existingBill) ? existingBill : null;
    const gasOld: GasBillDto | null = existingBill && isGasBill(existingBill) ? existingBill : null;

    const req: Record<string, unknown> = {};
    /** Overlay: defined form value wins; otherwise fall back to the existing bill's value. */
    const set = (key: string, formVal: unknown, oldVal?: unknown) => {
      const v = formVal !== undefined ? formVal : oldVal;
      if (v !== undefined) {
        req[key] = v;
      }
    };

    // --- General ---
    set('billStartDate', toApiDate(data.billingPeriodStart), toApiDate(general?.billStartDate));
    set('billEndDate', toApiDate(data.billingPeriodEnd), toApiDate(general?.billEndDate));
    set('periodBillTotal', num(data.totalAmount), general?.periodBillTotal);
    set('billType', toApiBillType(data.billType, data.isDualBilling), general?.billType);

    // --- Identification ---
    set('supplierId', data.supplierId, ident?.supplierId);
    set('energyPlanId', data.energyPlanId, ident?.energyPlanId);
    set('utilityId', data.utilityId ?? meterDet.utilityId ?? undefined, ident?.utilityId);
    set('rateClassId', undefined, ident?.rateClassId ?? meterDet.utilityRateScheduleId ?? undefined);
    req['unitId'] = this.resolveUnitId(data, meterDet, existingBill);

    // --- Usage ---
    set('totalUsage', num(data.usageAmount), usage?.totalUsage);

    // A section the user switched off is saved as null (and excluded from
    // validation). On EDIT, null clears any stored value via the JSON Merge
    // Patch; on ADD it stores null. The form keeps the disabled fields' values
    // only until save, so toggling back on restores them.
    const supplyOff = data.supplyDisabled === true;
    const distOff = data.distributionDisabled === true;

    // --- Supply (standard) ---
    // In budgeting mode the detailed tax bucket (supplySalesTaxes) is the source of truth
    // for supplyTax; the flat supplyTaxes number is the standard-mode field.
    const supplyCharges = supplyOff ? null : num(data.supplyCharges);
    const supplyTax = supplyOff ? null
      : ((isBudgetingMode ? groupTotal(data.supplySalesTaxes) : undefined) ?? num(data.supplyTaxes));
    set('supplyCharges', supplyCharges, supply?.supplyCharges);
    set('supplyTax', supplyTax, supply?.supplyTax);
    // supplyTotal: prefer the explicit form total (kept in sync by the editor / extraction);
    // derive charges + taxes when absent — mirrors the portfolio add-bill helpers.
    const supplyTotal = supplyOff ? null
      : (num(data.supplyTotalCost)
        ?? (supplyCharges !== undefined || supplyTax !== undefined
          ? (supplyCharges ?? 0) + (supplyTax ?? 0) : undefined));
    set('supplyTotal', supplyTotal, supply?.supplyTotal);

    // --- Distribution (standard) ---
    const distCharges = distOff ? null : num(data.distributionCharges);
    const distTax = distOff ? null
      : ((isBudgetingMode ? groupTotal(data.distributionTaxCharges) : undefined) ?? num(data.distributionTaxes));
    set('distributionCharges', distCharges, dist?.distributionCharges);
    set('distributionTax', distTax, dist?.distributionTax);
    const distTotal = distOff ? null
      : (num(data.distributionTotalCost)
        ?? (distCharges !== undefined || distTax !== undefined
          ? (distCharges ?? 0) + (distTax ?? 0) : undefined));
    set('distributionTotal', distTotal, dist?.distributionTotal);

    if (isElec) {
      const eu = elecOld?.usageInformation;
      const es = elecOld?.supplyInformation;
      const ed = elecOld?.distributionInformation;

      // Demand (kW) is the one numeric input the form blanks to undefined rather
      // than 0, so an absent value means "no demand", not "untouched". Send null
      // so the merge patch clears a stored value on EDIT (DEV-15535); on ADD the
      // backend stores null, the same as omitting it.
      set('demand', num(data.demandKw) ?? null);

      // Interval (TOU) usages
      set('onPeakUsage', num(data.onPeakUsage), eu?.onPeakUsage);
      set('onPeakCost', num(data.onPeakCost), eu?.onPeakCost);
      set('intermediateUsage', num(data.intermediateUsage), eu?.intermediateUsage);
      set('intermediateCost', num(data.intermediateCost), eu?.intermediateCost);
      set('offPeakUsage', num(data.offPeakUsage), eu?.offPeakUsage);
      set('offPeakCost', num(data.offPeakCost), eu?.offPeakCost);

      if (isBudgetingMode) {
        // Supply buckets (ChargeGroup.total → numeric API field). A disabled side
        // is nulled so its buckets can't re-derive the charges we just cleared.
        set('contractCharges', supplyOff ? null : groupTotal(data.supplyContractCharges), es?.contractCharges);
        set('energyCharges', supplyOff ? null : groupTotal(data.supplyEnergyCharges), es?.energyCharges);
        set('capacityCharges', supplyOff ? null : groupTotal(data.supplyCapacityCharges), es?.capacityCharges);
        set('transmissionCharges', supplyOff ? null : groupTotal(data.supplyTransmissionCharges), es?.transmissionCharges);
        set('marketCharges', supplyOff ? null : groupTotal(data.supplyMarketCharges), es?.marketCharges);
        set('lineLossCharges', supplyOff ? null : groupTotal(data.supplyLineLossCharges), es?.lineLossCharges);
        set('supplyMisc', supplyOff ? null : groupTotal(data.supplyMiscCharges), es?.supplyMisc);
        set('grtSut', supplyOff ? null : groupTotal(data.supplyGrtSut), es?.grtSut);

        // Distribution buckets
        set('distributionCustomerCharges', distOff ? null : groupTotal(data.distributionCustomerCharges), ed?.distributionCustomerCharges);
        set('distributionEnergyCharges', distOff ? null : groupTotal(data.distributionEnergyCharges), ed?.distributionEnergyCharges);
        set('distributionDemandCharges', distOff ? null : groupTotal(data.distributionDemandCharges), ed?.distributionDemandCharges);
        set('distributionMisc', distOff ? null : groupTotal(data.distributionMiscCharges), ed?.distributionMisc);
      }
    } else {
      // Gas budgeting buckets (ChargeGroup.total → numeric gas API field).
      const gs = gasOld?.supplyInformation;
      const gd = gasOld?.distributionInformation;
      if (isBudgetingMode) {
        // Supply
        set('contractCharges', supplyOff ? null : groupTotal(data.supplyContractCharges), gs?.contractCharges);
        set('supplyCustomerCharges', supplyOff ? null : groupTotal(data.supplyCustomerCharges), gs?.supplyCustomerCharges);
        set('supplyGasCharges', supplyOff ? null : groupTotal(data.supplyGasCharges), gs?.supplyGasCharges);
        set('supplyOffPeakCharges', supplyOff ? null : groupTotal(data.supplyOffPeakCharges), gs?.supplyOffPeakCharges);
        set('supplyRateClassCharges', supplyOff ? null : groupTotal(data.supplyRateClassCharges), gs?.supplyRateClassCharges);
        set('supplyMisc', supplyOff ? null : groupTotal(data.supplyMiscCharges), gs?.supplyMisc);
        set('supplyGrtSut', supplyOff ? null : groupTotal(data.supplyGrtSut), gs?.supplyGrtSut);

        // Distribution
        set('distributionCustomerCharges', distOff ? null : groupTotal(data.distributionCustomerCharges), gd?.distributionCustomerCharges);
        set('distributionGasCharges', distOff ? null : groupTotal(data.distributionGasCharges), gd?.distributionGasCharges);
        set('distributionDeliveryCharges', distOff ? null : groupTotal(data.distributionDeliveryCharges), gd?.distributionDeliveryCharges);
        set('distributionOffPeakCharges', distOff ? null : groupTotal(data.distributionOffPeakCharges), gd?.distributionOffPeakCharges);
        set('distributionBalancingCharges', distOff ? null : groupTotal(data.distributionBalancingCharges), gd?.distributionBalancingCharges);
        set('distributionRateClassCharges', distOff ? null : groupTotal(data.distributionRateClassCharges), gd?.distributionRateClassCharges);
        set('distributionMisc', distOff ? null : groupTotal(data.distributionMiscCharges), gd?.distributionMisc);
        set('distributionGrtSut', distOff ? null : groupTotal(data.distributionGrtSut), gd?.distributionGrtSut);
      }
    }

    // --- Charge-bucket line items (budgeting mode only — standard mode has no buckets) ---
    // Each JSON column is a flat array per section; the originating ChargeGroup bucket key is
    // encoded in lineItemData.category so the load path can regroup. supplyTax/distributionTax
    // tax buckets (supplySalesTaxes / distributionTaxCharges) are part of their section's array.
    // generalLineItems / usageLineItems have no ParsedBillData source — sent empty for shape
    // completeness (the backend treats them as flat arrays too).
    if (isBudgetingMode) {
      const supplyItems = collectLineItems(data, SUPPLY_BUCKET_KEYS);
      const distributionItems = collectLineItems(data, DISTRIBUTION_BUCKET_KEYS);
      // Only emit a bucket array when there is something to write, so a JSON Merge Patch with
      // no line items doesn't clobber existing server-side line items with an empty array.
      // A disabled side is the exception: on EDIT, an empty array clears its stored items.
      if (supplyOff) {
        if (existingBill) req['supplyLineItems'] = [];
      } else if (supplyItems.length > 0) {
        req['supplyLineItems'] = supplyItems;
      }
      if (distOff) {
        if (existingBill) req['distributionLineItems'] = [];
      } else if (distributionItems.length > 0) {
        req['distributionLineItems'] = distributionItems;
      }
    }

    // --- Files (only when newly uploaded this session) ---
    if (opts.tempFileId) {
      req['tempFileId'] = opts.tempFileId;
    }
    if (opts.supplierTempFileId) {
      req['supplierTempFileId'] = opts.supplierTempFileId;
    }

    // --- Bill AI link ---
    if (opts.billAiId != null) {
      req['billAiId'] = opts.billAiId;
    }

    // Strip backend-derived fields and ids — never sent on create or patch.
    for (const k of BILL_REQUEST_OMIT_FIELDS) {
      delete req[k];
    }

    return req as Partial<BillRequest>;
  }

  // ===========================================================================
  // SAVE — thin wrappers over BillsV4Service
  // ===========================================================================

  /** Creates a new bill on the meter described by `meterDet` (ADD mode). */
  saveNew(data: ParsedBillData, meterDet: MeterDetailsV4Model, isBudgetingMode: boolean,
          files?: BillSaveFileRefs): Observable<BillDto> {
    const request = this.buildBillRequest(data, { meterDet, isBudgetingMode, ...files });
    const createRequest = {
      createdByUserId: this.authSvc.getCurrentUserId(),
      ...request,
    } as BillCreateRequest;
    return this.billsV4Service.createBills(
      meterDet.commodityType, meterDet.customerId, meterDet.serviceAddressId, meterDet.id, createRequest);
  }

  /** Patches `existingBill` with the form values, preserving untouched fields (EDIT mode). */
  saveExisting(data: ParsedBillData, meterDet: MeterDetailsV4Model, existingBill: BillDto,
               isBudgetingMode: boolean, files?: BillSaveFileRefs): Observable<BillDto> {
    const patch = this.buildBillRequest(data, { meterDet, isBudgetingMode, existingBill, ...files });
    return this.billsV4Service.updateBills(
      meterDet.commodityType, meterDet.customerId, meterDet.serviceAddressId, meterDet.id,
      existingBill.id, patch);
  }

  // ===========================================================================
  // Internals
  // ===========================================================================

  /**
   * Resolves the platform unitId for the request.
   * - The form stamps the chosen unit's id (data.usageUnitId), so honor it first
   *   for both commodities — that's what lets electricity persist MWh instead of
   *   always falling back to kWh.
   * - Electricity fallback: the existing bill's unit, then kWh.
   * - Gas fallback: map the usage unit name via {@link GasUnitType}, then the
   *   existing bill's unit, then the meter's default, then DTH.
   */
  private resolveUnitId(data: ParsedBillData, meterDet: MeterDetailsV4Model, existingBill?: BillDto): number {
    if (meterDet.commodityType === CommodityType.Electricity) {
      return data.usageUnitId
        ?? existingBill?.identificationInformation?.unitId
        ?? ELEC_KWH_UNIT_ID;
    }
    return data.usageUnitId
      ?? gasUnitIdFromName(data.usageUnit)
      ?? existingBill?.identificationInformation?.unitId
      ?? existingBill?.unitDto?.id
      ?? meterDet.defaultUtilityUnitId
      ?? GasUnitType.DTH;
  }
}

// =============================================================================
// Module-private helpers (exported for direct unit testing)
// =============================================================================

/** Platform unitId for kWh — electricity bills are always kWh. */
export const ELEC_KWH_UNIT_ID = 1;

/**
 * ParsedBillData.billType → API {@link BillType} (the billing method).
 * The API stores the method, not which document a dual bill is — so an explicit
 * dual flag forces DUAL even for a utility document. Otherwise mirrors
 * bill-grouping.util: a utility bill is CONSOLIDATED, supplier/unknown are DUAL.
 */
export function toApiBillType(billType: ParsedBillType | undefined, isDualBilling?: boolean): BillType | undefined {
  if (isDualBilling) {
    return BillType.DUAL;
  }
  if (!billType) {
    return undefined;
  }
  return billType === 'utility' ? BillType.CONSOLIDATED : BillType.DUAL;
}

/** API {@link BillType} → ParsedBillData.billType. CONSOLIDATED → 'utility', DUAL → 'supplier'. */
export function fromApiBillType(billType: BillType | undefined): ParsedBillType {
  if (!billType) {
    return 'unknown';
  }
  return billType === BillType.CONSOLIDATED ? 'utility' : 'supplier';
}

/**
 * Converts an ISO-ish date string (or Date) to the `YYYY-MM-DD` LocalDate string the
 * bills API expects. Date-only prefixes are taken verbatim to avoid timezone shifting.
 */
export function toApiDate(value: string | Date | undefined | null): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.substring(0, 10);
    }
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-MM-DD') : undefined;
  }
  return moment(value).format('YYYY-MM-DD');
}

/** Normalizes an API date string for the form (ISO `YYYY-MM-DD`). */
function toIsoDateOnly(value: string | undefined): string | undefined {
  return toApiDate(value);
}

/** Builds a ChargeGroup from a stored numeric bucket total (no line items — see class doc). */
function toChargeGroup(total: number | undefined | null): ChargeGroup | undefined {
  return total != null ? { total, lineItems: [] } : undefined;
}

/** Reads a ChargeGroup total for the request; undefined when the group/total is absent. */
function groupTotal(group: ChargeGroup | undefined): number | undefined {
  return num(group?.total);
}

/**
 * ParsedBillData ChargeGroup field keys whose line items serialize into the SUPPLY JSON
 * column. Each item's {@link BillLineItem.lineItemData}.category records which of these it
 * came from so {@link applyLineItemsToGroups} can regroup the flat array on load.
 */
export const SUPPLY_BUCKET_KEYS = [
  'supplyContractCharges',
  'supplyEnergyCharges',
  'supplyCapacityCharges',
  'supplyTransmissionCharges',
  'supplyMarketCharges',
  'supplyLineLossCharges',
  'supplyGrtSut',
  'supplyMiscCharges',
  'supplySalesTaxes',
  // Gas-specific supply buckets
  'supplyCustomerCharges',
  'supplyGasCharges',
  'supplyOffPeakCharges',
  'supplyRateClassCharges',
] as const satisfies readonly (keyof ParsedBillData)[];

/** ParsedBillData ChargeGroup field keys whose line items serialize into the DISTRIBUTION column. */
export const DISTRIBUTION_BUCKET_KEYS = [
  'distributionDemandCharges',
  'distributionEnergyCharges',
  'distributionCustomerCharges',
  'distributionMiscCharges',
  'distributionTaxCharges',
  // Gas-specific distribution buckets
  'distributionGasCharges',
  'distributionDeliveryCharges',
  'distributionOffPeakCharges',
  'distributionBalancingCharges',
  'distributionRateClassCharges',
  'distributionGrtSut',
] as const satisfies readonly (keyof ParsedBillData)[];

type ChargeGroupKey = (typeof SUPPLY_BUCKET_KEYS)[number] | (typeof DISTRIBUTION_BUCKET_KEYS)[number];

/** ChargeLineItem → backend BillLineItem, tagging the originating bucket in lineItemData.category. */
function toBillLineItem(item: ChargeLineItem, category: ChargeGroupKey): BillLineItem {
  const out: BillLineItem = { lineItemData: { category } };
  if (item.description != null) {
    out.name = item.description;
  }
  const cost = num(item.amount);
  if (cost !== undefined) {
    out.cost = cost;
  }
  const usage = num(item.usage);
  if (usage !== undefined) {
    out.usage = usage;
  }
  if (item.unit != null) {
    out.unit = item.unit;
  }
  const rate = num(item.rate);
  if (rate !== undefined) {
    out.rate = rate;
  }
  return out;
}

/** backend BillLineItem → ChargeLineItem (reverse of {@link toBillLineItem}). */
function fromBillLineItem(item: BillLineItem): ChargeLineItem {
  return {
    description: item.name ?? '',
    amount: num(item.cost) ?? 0,
    usage: num(item.usage),
    unit: item.unit,
    rate: num(item.rate),
  };
}

/** Flattens the ChargeGroups named by `keys` into a single tagged BillLineItem array. */
export function collectLineItems(data: ParsedBillData, keys: readonly ChargeGroupKey[]): BillLineItem[] {
  const out: BillLineItem[] = [];
  for (const key of keys) {
    const group = data[key] as ChargeGroup | undefined;
    if (!group?.lineItems?.length) {
      continue;
    }
    for (const item of group.lineItems) {
      out.push(toBillLineItem(item, key));
    }
  }
  return out;
}

/**
 * Regroups a flat BillLineItem array back into the ChargeGroups named by `keys`, using each
 * item's lineItemData.category. Items are appended to the existing group's lineItems; if the
 * group does not yet exist (no numeric total) one is created with total 0. Items whose
 * category is missing/unknown are dropped (no faithful home).
 */
function applyLineItemsToGroups(
  data: ParsedBillData,
  items: BillLineItem[] | undefined,
  keys: readonly ChargeGroupKey[],
): void {
  if (!items?.length) {
    return;
  }
  const valid = new Set<string>(keys);
  for (const item of items) {
    const category = item.lineItemData?.category;
    if (!category || !valid.has(category)) {
      continue;
    }
    const key = category as ChargeGroupKey;
    let group = data[key] as ChargeGroup | undefined;
    if (!group) {
      group = { total: 0, lineItems: [] };
      (data[key] as ChargeGroup) = group;
    }
    group.lineItems.push(fromBillLineItem(item));
  }
}

/** Normalizes a numeric form value: null/NaN/undefined → undefined (omit from payload). */
function num(v: number | undefined | null): number | undefined {
  return v != null && Number.isFinite(v) ? v : undefined;
}

/**
 * Maps a free-text gas usage-unit name (e.g. "therms", "Dth", "CCF") to the platform
 * {@link GasUnitType} unitId. Returns undefined when unrecognized.
 */
export function gasUnitIdFromName(unit: string | undefined): number | undefined {
  if (!unit) {
    return undefined;
  }
  const key = unit.trim().toUpperCase();
  switch (key) {
    case 'DTH':
    case 'DEKATHERM':
    case 'DEKATHERMS':
      return GasUnitType.DTH;
    case 'TH':
    case 'THERM':
    case 'THERMS':
      return GasUnitType.TH;
    case 'MMBTU':
      return GasUnitType.MMBTU;
    case 'CCF':
      return GasUnitType.CCF;
    case 'MCF':
      return GasUnitType.MCF;
    case 'GJ':
      return GasUnitType.GJ;
    case 'M3':
    case 'M³':
      return GasUnitType.M3;
    default:
      return undefined;
  }
}

/**
 * Line items for one side of an import payload. Budgeting mode only, matching buildBillRequest;
 * undefined when there are none so the key is omitted.
 */
export function lineItemsForImport(
  data: ParsedBillData | undefined,
  keys: readonly ChargeGroupKey[],
  isBudgetingMode: boolean
): BillLineItem[] | undefined {
  const items = data && isBudgetingMode ? collectLineItems(data, keys) : [];
  return items.length > 0 ? items : undefined;
}

/** Platform unitIds that denote gas usage — kWh is 1, {@link GasUnitType} occupies 3-9. */
const GAS_UNIT_IDS: ReadonlySet<number> = new Set<number>([
  GasUnitType.DTH,
  GasUnitType.TH,
  GasUnitType.MMBTU,
  GasUnitType.CCF,
  GasUnitType.MCF,
  GasUnitType.GJ,
  GasUnitType.M3,
]);

/**
 * Unit-name fragments that identify gas usage, for units printed with decoration
 * ("CCF (100 cu ft)", "therms used") that {@link gasUnitIdFromName} — which matches
 * whole names — cannot resolve.
 */
const GAS_UNIT_FRAGMENTS = ['dth', 'dekatherm', 'therm', 'mmbtu', 'ccf', 'mcf'];

/**
 * The platform gas unitId a bill's usage resolves to: the unitId stamped by the bill form when
 * it is a gas unit, else the unit name via {@link gasUnitIdFromName}. Undefined when neither
 * resolves (no unit, or a printed one like "CCF (100 cu ft)") — the import cannot send a unit
 * for that bill, and the API rejects a gas bill without one.
 */
export function resolveGasUnitId(unitId: number | null | undefined, unitName?: string): number | undefined {
  return unitId != null && GAS_UNIT_IDS.has(unitId) ? unitId : gasUnitIdFromName(unitName);
}

/**
 * Display label for a gas bill's unit, canonicalised through the platform unit so spelling and
 * case variants of one unit ('therms', 'Therm', 'TH') read as the same unit; an unrecognised
 * name is shown as written, and no unit at all reads as Dth.
 */
export function gasUnitLabel(unitId: number | null | undefined, unitName?: string): string {
  const id = resolveGasUnitId(unitId, unitName);
  if (id !== undefined) {
    return GAS_UNIT_LABEL[GasUnitType[id] as GasUnitName];
  }
  const raw = unitName?.trim();
  return raw ? raw : GAS_UNIT_LABEL.DTH;
}

/**
 * True when a bill's usage is measured in gas: the platform unitId stamped by the bill
 * form first, then the unit name via {@link gasUnitIdFromName}, then a fragment match.
 *
 * Fallback only — a bill's extracted `commodityType` is authoritative and should be
 * checked before this.
 */
export function isGasUsageUnit(unitId: number | null | undefined, unitName?: string): boolean {
  if (unitId != null && GAS_UNIT_IDS.has(unitId)) {
    return true;
  }
  if (gasUnitIdFromName(unitName) !== undefined) {
    return true;
  }
  const key = unitName?.toLowerCase().trim() ?? '';
  return key.length > 0 && GAS_UNIT_FRAGMENTS.some(fragment => key.includes(fragment));
}

/**
 * Platform unitId for electricity usage. There is no electricity counterpart to
 * {@link GasUnitType} — the `unit` table has a single usage unit for electricity — so
 * this names the id rather than leaving a bare 1 at the call sites.
 */


/**
 * True when a bill's usage is measured in kWh: the platform unitId stamped by the bill
 * form first, then the unit name. The counterpart to {@link isGasUsageUnit}, and a
 * fallback in the same way — a bill's extracted `commodityType` is authoritative.
 */
export function isElectricUsageUnit(unitId: number | null | undefined, unitName?: string): boolean {
  if (unitId === ELEC_KWH_UNIT_ID) {
    return true;
  }
  return (unitName ?? '').toLowerCase().includes('kwh');
}
